// src/utils/examGroups.js
// Builds an ordered list of "question groups" from a section structure -
// the unit the exam-taking screen navigates and shows exhibits/requirements/
// responses by. A defined group (sectionStructure.groups[]) combines several
// marking sections (e.g. Question 2's parts (i) and (ii)) into one screen
// with one shared response; any section not listed in a group becomes its
// own singleton group, using the section's own id as the group id - so a
// homework type with no groups defined behaves exactly as before this
// feature existed.
//
// Marking is completely unaffected: sections stay the atomic mark-allocation
// unit in FirebaseMarker.jsx regardless of how they're grouped for display.
//
// Mirrored in functions/index.js (buildExamGroups) since Cloud Functions is
// a separate deployable with no shared module boundary to this file - keep
// the two in sync if this logic ever changes.
export function buildExamGroups(sectionStructure) {
  const sections = sectionStructure?.sections || [];
  const definedGroups = sectionStructure?.groups || [];
  const groupForSection = new Map();
  definedGroups.forEach(g => {
    (g.sectionIds || []).forEach(id => groupForSection.set(id, g));
  });

  const result = [];
  const seenGroupIds = new Set();
  sections.forEach(section => {
    const definedGroup = groupForSection.get(section.id);
    if (definedGroup) {
      if (seenGroupIds.has(definedGroup.id)) return;
      seenGroupIds.add(definedGroup.id);
      const memberSections = (definedGroup.sectionIds || [])
        .map(id => sections.find(s => s.id === id))
        .filter(Boolean);
      if (memberSections.length === 0) return;
      result.push({
        id: definedGroup.id,
        name: definedGroup.name || memberSections.map(s => s.name).join(' & '),
        breakdown: definedGroup.breakdown || '',
        // Separate, optional text for the standalone pre-question Breakdown
        // Screen (src/components/ExamQuestionBreakdown.jsx) - independent of
        // `breakdown` above, which feeds the split screen's QUESTION
        // INFORMATION pane. Not used server-side (functions/index.js's
        // mirrored buildExamGroups only reads .id/.sections), so it isn't
        // mirrored there.
        breakdownScreenText: definedGroup.breakdownScreenText || '',
        // Per-question override for whether the standalone Breakdown Screen
        // appears before this question at all (independent of what text it
        // would show) - lets one question in a homework type opt out while
        // others still get it, e.g. a second question that shares the first
        // question's content and doesn't need its own intro screen.
        // Undefined/absent means shown, so existing groups (defined before
        // this override existed) keep their current behaviour.
        showBreakdownScreen: definedGroup.showBreakdownScreen,
        // The whole question's own exhibits/requirements/starting content -
        // when filled in, used instead of the member sections' (see
        // groupExhibits etc. below). Absent on every group set up before
        // 2026-10-11, so those carry on using their sections' content.
        ...groupOwnContent(definedGroup),
        sections: memberSections
      });
    } else {
      result.push({ id: section.id, name: section.name, sections: [section] });
    }
  });
  return result;
}

// A combined question's own content fields, copied onto its exam group.
// Mirrored in functions/index.js.
export function groupOwnContent(definedGroup) {
  return {
    exhibits: definedGroup.exhibits || [],
    requirements: definedGroup.requirements || [],
    startingGridJson: definedGroup.startingGridJson || null,
    startingHtml: definedGroup.startingHtml || '',
    includeBlankSpreadsheet: !!definedGroup.includeBlankSpreadsheet,
    includeBlankWordProcessor: !!definedGroup.includeBlankWordProcessor
  };
}

// What students see for each item on an exam group (one on-screen
// question): the group's own, if it has any, otherwise every member
// section's together - so a group with nothing of its own behaves exactly
// as it did before groups could have content.
export function groupExhibits(group) {
  return (group.exhibits || []).length ? group.exhibits : group.sections.flatMap(s => s.exhibits || []);
}

export function groupRequirements(group) {
  return (group.requirements || []).length ? group.requirements : group.sections.flatMap(s => s.requirements || []);
}

// How many marks an exam group (one on-screen "question") is worth -
// technical + professional, respecting technicalMarkGroups/
// professionalMarkGroups the same way marking does (see calculateMaxMarks
// in FirebaseMarker.jsx / calculateHomeworkTotalMarks in
// HeadMarkerDashboard.jsx) - NOT just summing each section's legacy
// maxMark, which ignores professional marks and any shared-cap group
// entirely and was showing the wrong number on the exam screen's header.
// A mark group only needs to share ONE section with this exam group to
// count in full here - in practice a group's sections and an exam
// question's sections are the same boundary, this just doesn't crash or
// silently drop marks in the rare case they're not.
export function calculateGroupMarks(group, sectionStructure) {
  const sectionIds = new Set(group.sections.map(s => s.id));
  const techGroups = (sectionStructure?.technicalMarkGroups || []).filter(g => g.sections.some(id => sectionIds.has(id)));
  const profGroups = (sectionStructure?.professionalMarkGroups || []).filter(g => g.sections.some(id => sectionIds.has(id)));
  const techGroupedIds = new Set(techGroups.flatMap(g => g.sections));
  const profGroupedIds = new Set(profGroups.flatMap(g => g.sections));

  const perSection = group.sections.reduce((sum, s) =>
    sum + (techGroupedIds.has(s.id) ? 0 : (s.maxTechnical ?? s.maxMark ?? 0))
        + (profGroupedIds.has(s.id) ? 0 : (s.maxProfessional ?? 1)), 0);

  return perSection
    + techGroups.reduce((sum, g) => sum + g.totalMarks, 0)
    + profGroups.reduce((sum, g) => sum + g.totalMarks, 0);
}
